import React, { useState, useEffect } from 'react';
import IconButton from './icons/IconButton';
import { ICON } from '../icons/iconIds';
import SelkirkIcon from './icons/SelkirkIcon';
import IconText from './icons/IconText';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import { qaqcService } from '../services/qaqcService';
import ControlStandardsChart from './qaqc/charts/ControlStandardsChart';
import StandardsDriftChart from './qaqc/charts/StandardsDriftChart';
import StandardsHeatmapChart from './qaqc/charts/StandardsHeatmapChart';
import StandardsTrendChart from './qaqc/charts/StandardsTrendChart';
import BlanksContaminationChart from './qaqc/charts/BlanksContaminationChart';
import DuplicatesPrecisionChart from './qaqc/charts/DuplicatesPrecisionChart';
import SurroundingSamplesTable from './qaqc/charts/SurroundingSamplesTable';
import QAQCReportsTab from './qaqc/reports/QAQCReportsTab';
import ChartImageDownloadButton from './qaqc/ChartImageDownloadButton';
import PrintStandardsDriftChart from './qaqc/reports/print/PrintStandardsDriftChart';
import PrintStandardsTrendChart from './qaqc/reports/print/PrintStandardsTrendChart';
import PrintBlanksChart from './qaqc/reports/print/PrintBlanksChart';
import PrintDuplicatesChart from './qaqc/reports/print/PrintDuplicatesChart';
import PrintControlStandardsChart from './qaqc/reports/print/PrintControlStandardsChart';
import { effectiveWithinRpdDynamic } from '../utils/qaqc/chartDataTransform';
import {
  consumePendingFailureNav,
  readFailureNavFromHash,
} from '../utils/qaqcDeepLink';
import './QAQC.css';

/** Map failure_group (DB or UI label) to chart tab id */
const mapFailureGroupToTab = (failureGroup) => {
  const g = String(failureGroup || '')
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, '_');
  if (g === 'control_standard' || g === 'standard' || g.includes('standard')) return 'controls';
  if (g === 'blank' || g.includes('blank')) return 'blanks-analysis';
  if (g === 'field_duplicate' || g === 'duplicate' || g.includes('duplicate')) {
    return 'duplicates-precision';
  }
  return null;
};

/** Normalize element labels (CU/cu → Cu) for chart chip filters */
const normalizeQaqcElement = (element) => {
  if (element == null || String(element).trim() === '') return null;
  const upper = String(element).trim().toUpperCase();
  if (upper === 'AU' || upper === 'AG' || upper === 'CU') {
    return upper.charAt(0) + upper.slice(1).toLowerCase();
  }
  return String(element).trim();
};

const QAQC = ({ user }) => {
  const [dashboardData, setDashboardData] = useState({
    sampleCountReconcile: [],
    controlStandards: [],
    fieldDuplicates: [],
    qaqcFailures: [],
    // Bias/Drift Analysis Data
    standardsDrift: [],
    standardsBiasWorkorder: [],
    standardsBiasMonth: [],
    blanksUnified: [],
    blanksDrift: [],
    blanksBiasWorkorder: [],
    duplicatesDrift: [],
    duplicatesBiasWorkorder: []
  });
  
  // Granular loading state tracking
  const [loadingStates, setLoadingStates] = useState({
    dashboardSummary: false,
    qaqcFailures: false,
    sampleCountReconcile: false,
    controlStandards: false,
    fieldDuplicates: false,
    standardsDrift: false,
    standardsBiasWorkorder: false,
    standardsBiasMonth: false,
    blanksDrift: false,
    duplicatesDrift: false
  });
  
  const [loadedDatasets, setLoadedDatasets] = useState(new Set());
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [showResolved, setShowResolved] = useState(false);
  const [refreshingFailures, setRefreshingFailures] = useState(false);
  // { tab, sampleNumber, element } — consumed by destination chart sample filter
  const [pendingSampleNav, setPendingSampleNav] = useState(null);
  const [failureDeepLink, setFailureDeepLink] = useState(null);
  
  // Chart state
  const [chartElement, setChartElement] = useState('Cu');
  const [chartCRM, setChartCRM] = useState('all');
  const [selectedSample, setSelectedSample] = useState(null);
  
  // Blank chart state (used by blanks-analysis tab)
  const [selectedBlank, setSelectedBlank] = useState(null);
  
  // Bias/Drift Analysis state
  const [driftElement, setDriftElement] = useState('Cu');
  const [driftCRM, setDriftCRM] = useState('all');
  const [heatmapElement, setHeatmapElement] = useState('all');
  const [trendElement, setTrendElement] = useState('all');
  const [trendCRM, setTrendCRM] = useState('all');
  const [blanksContamElement, setBlanksContamElement] = useState('Ag');
  const [duplicatesPrecisionElement, setDuplicatesPrecisionElement] = useState('Cu');
  const [selectedDuplicate, setSelectedDuplicate] = useState(null);
  
  // Surrounding samples state
  const [surroundingSamples, setSurroundingSamples] = useState(null);
  const [loadingSurrounding, setLoadingSurrounding] = useState(false);
  
  // Workorder notes state
  const [workorderNotes, setWorkorderNotes] = useState({});
  const [editingNoteFor, setEditingNoteFor] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Lazy load data for specific tabs
  const loadDataForTab = async (tabName) => {
    const dataRequirements = {
      'overview': ['dashboardSummary'],  // Optimized: only load pre-calculated summary
      'failures': ['qaqcFailures'],
      'standards-drift': ['standardsDrift'],
      'standards-heatmap': ['standardsBiasWorkorder'],
      'standards-trend': ['standardsBiasMonth'],
      'blanks-analysis': ['blanksDrift'],
      'duplicates-precision': ['duplicatesDrift'],
      'samples': ['sampleCountReconcile', 'qaqcFailures'],  // Also load failures to highlight rows
      'controls': ['controlStandards'],
      'duplicates': ['fieldDuplicates'],
      'reports': ['dashboardSummary', 'sampleCountReconcile', 'controlStandards', 'standardsDrift', 'standardsBiasMonth', 'blanksDrift', 'duplicatesDrift', 'fieldDuplicates', 'qaqcFailures']  // Load all data for comprehensive reports
    };

    const requiredData = dataRequirements[tabName] || [];
    const datasetsToLoad = requiredData.filter(dataset => !loadedDatasets.has(dataset));
    
    if (datasetsToLoad.length === 0) {
      console.log(`✅ Data already loaded for tab: ${tabName}`);
      return;
    }

    console.log(`🔄 Loading data for tab: ${tabName}`, datasetsToLoad);

    // Update loading states
    const newLoadingStates = { ...loadingStates };
    datasetsToLoad.forEach(dataset => {
      newLoadingStates[dataset] = true;
    });
    setLoadingStates(newLoadingStates);

    try {
      // Load datasets in parallel
      const loadPromises = datasetsToLoad.map(async (dataset) => {
        let data;
        switch(dataset) {
          case 'dashboardSummary':
            data = await qaqcService.getDashboardSummary();
            // Store in separate state, not dashboardData array
            setDashboardSummary(data);
            break;
          case 'qaqcFailures':
            data = await qaqcService.getQAQCFailures();
            break;
          case 'sampleCountReconcile':
            data = await qaqcService.getSampleCountReconcile();
            // Also load workorder notes for this view
            try {
              const notesData = await qaqcService.getWorkorderNotes();
              const notesMap = {};
              notesData.forEach(n => {
                notesMap[n.workorder_id] = n.note;
              });
              setWorkorderNotes(notesMap);
              console.log(`✅ Loaded ${notesData.length} workorder notes`);
            } catch (notesError) {
              console.error('Failed to load workorder notes:', notesError);
              // Continue even if notes fail - it's not critical
            }
            break;
          case 'controlStandards':
            data = await qaqcService.getControlStandards();
            break;
          case 'fieldDuplicates':
            data = await qaqcService.getFieldDuplicates();
            break;
          case 'standardsDrift':
            data = await qaqcService.getStandardsDrift();
            break;
          case 'standardsBiasWorkorder':
            data = await qaqcService.getStandardsBiasWorkorder();
            break;
          case 'standardsBiasMonth':
            data = await qaqcService.getStandardsBiasMonth();
            break;
          case 'blanksDrift':
            data = await qaqcService.getBlanksDrift();
            break;
          case 'duplicatesDrift':
            data = await qaqcService.getDuplicatesDrift();
            break;
          default:
            data = [];
        }
        return { dataset, data };
      });

      const results = await Promise.all(loadPromises);

      // Update dashboard data (skip dashboardSummary as it's in separate state)
      setDashboardData(prev => {
        const updated = { ...prev };
        results.forEach(({ dataset, data }) => {
          if (dataset !== 'dashboardSummary') {
            updated[dataset] = data || [];
          }
        });
        return updated;
      });

      // Mark datasets as loaded
      setLoadedDatasets(prev => new Set([...prev, ...datasetsToLoad]));

      console.log(`✅ Loaded data for tab: ${tabName}`);
    } catch (err) {
      console.error(`❌ Error loading data for tab ${tabName}:`, err);
      setError(err.message || `Failed to load data for ${tabName}`);
    } finally {
      // Clear loading states
      const clearedStates = { ...loadingStates };
      datasetsToLoad.forEach(dataset => {
        clearedStates[dataset] = false;
      });
      setLoadingStates(clearedStates);
    }
  };

  useEffect(() => {
    const nav = consumePendingFailureNav() || readFailureNavFromHash();
    if (nav && (nav.tab === 'failures' || nav.failureId || nav.holeId || nav.sampleNumber)) {
      setActiveTab('failures');
      setFailureDeepLink(nav);
    }
  }, []);

  // Load data on component mount - only load overview essentials
  useEffect(() => {
    loadDataForTab('overview');
  }, []);

  // Load data when switching tabs
  useEffect(() => {
    loadDataForTab(activeTab);
  }, [activeTab]);

  // Legacy function - kept for backward compatibility (e.g., manual refresh)
  const loadDashboardData = async () => {
    try {
      setError(null);
      console.log('⚠️ Loading all dashboard data (legacy method)');
      
      const data = await qaqcService.getDashboardData();
      
      setDashboardData({
        sampleCountReconcile: data.sampleCountReconcile || [],
        controlStandards: data.controlStandards || [],
        fieldDuplicates: data.fieldDuplicates || [],
        qaqcFailures: data.qaqcFailures || [],
        // Bias/Drift Analysis Data
        standardsDrift: data.standardsDrift || [],
        standardsBiasWorkorder: data.standardsBiasWorkorder || [],
        standardsBiasMonth: data.standardsBiasMonth || [],
        blanksUnified: data.blanksUnified || [],
        blanksDrift: data.blanksDrift || [],
        blanksBiasWorkorder: data.blanksBiasWorkorder || [],
        duplicatesDrift: data.duplicatesDrift || [],
        duplicatesBiasWorkorder: data.duplicatesBiasWorkorder || []
      });
      
      // Mark all datasets as loaded
      setLoadedDatasets(new Set([
        'sampleCountReconcile', 'controlStandards', 'fieldDuplicates',
        'qaqcFailures', 'standardsDrift',
        'standardsBiasWorkorder', 'standardsBiasMonth', 'blanksUnified',
        'blanksDrift', 'blanksBiasWorkorder', 'duplicatesDrift',
        'duplicatesBiasWorkorder'
      ]));
      
      console.log('✅ QAQC data loaded:', data);
    } catch (err) {
      console.error('❌ Error loading QAQC data:', err);
      setError(err.message || 'Failed to load QAQC data');
    }
  };

  const handleRefreshFailures = async () => {
    try {
      setRefreshingFailures(true);
      
      console.log('🔄 Refreshing QAQC failures table...');
      const result = await qaqcService.refreshFailures();
      
      console.log('✅ Failures refreshed successfully:', result);
      
      // Reload qaqcFailures data if it was already loaded
      if (loadedDatasets.has('qaqcFailures')) {
        const data = await qaqcService.getQAQCFailures();
        setDashboardData(prev => ({
          ...prev,
          qaqcFailures: data || []
        }));
      }
      
      // Reload dashboard summary if it was already loaded
      if (loadedDatasets.has('dashboardSummary')) {
        const summary = await qaqcService.getDashboardSummary();
        setDashboardSummary(summary);
      }
      
      alert(`Failures table refreshed successfully!\n\n${result.message || 'All QAQC failures have been updated.'}`);
    } catch (err) {
      console.error('❌ Error refreshing failures:', err);
      alert(`Failed to refresh failures table:\n\n${err.message}`);
    } finally {
      setRefreshingFailures(false);
    }
  };

  const handleSampleClick = async (sample, sampleType) => {
    // Set selected sample
    if (sampleType === 'blank') {
      setSelectedBlank(sample);
    } else if (sampleType === 'duplicate') {
      setSelectedDuplicate(sample);
    } else if (sampleType === 'standard') {
      setSelectedSample(sample);
    }
    
    // Fetch surrounding samples
    let sampleId;
    if (sampleType === 'duplicate') {
      const rawId =
        sample.parent_sample ??
        sample.orig_sample_number ??
        sample.orig_sample_id ??
        sample.sample_id ??
        sample.sample_number;
      sampleId =
        rawId !== undefined && rawId !== null && String(rawId).trim() !== ''
          ? String(rawId).trim()
          : null;
    } else {
      const raw = sample.sample_id ?? sample.sample_number;
      sampleId =
        raw !== undefined && raw !== null && String(raw).trim() !== ''
          ? String(raw).trim()
          : null;
    }
    
    if (sampleId) {
      try {
        setLoadingSurrounding(true);
        const surrounding = await qaqcService.getSurroundingSamples(sampleId);
        setSurroundingSamples(surrounding);
        console.log('📍 Surrounding samples:', surrounding);
      } catch (err) {
        console.error('Failed to fetch surrounding samples:', err);
        setSurroundingSamples(null);
      } finally {
        setLoadingSurrounding(false);
      }
    }
  };

  const handleClearSelection = () => {
    setSelectedSample(null);
    setSelectedBlank(null);
    setSelectedDuplicate(null);
    setSurroundingSamples(null);
  };

  const handleCopyData = (selectedSample, surroundingSamples) => {
    if (!selectedSample) return;
    
    // Build detailed text output
    let output = '=== SELECTED SAMPLE DETAILS ===\n\n';
    output += `Sample Number: ${selectedSample.sample_number || selectedSample.sample_id}\n`;
    output += `Workorder ID: ${selectedSample.workorder_id || selectedSample.orig_workorder_id}\n`;
    output += `Hole ID: ${selectedSample.hole_id || 'N/A'}\n`;
    output += `Element: ${selectedSample.element}\n`;
    output += `Value: ${selectedSample.value || selectedSample.best_value || selectedSample.mean_value}\n`;
    output += `Z-Score: ${selectedSample.z_score}\n`;
    output += `QC Status: ${selectedSample.qc_status || 'N/A'}\n`;
    
    // Add type-specific fields
    if (selectedSample.crushing_carryover_pct) {
      output += `Carryover %: ${selectedSample.crushing_carryover_pct}\n`;
    }
    if (selectedSample.crm_code) {
      output += `CRM Code: ${selectedSample.crm_code}\n`;
      output += `Certified Value: ${selectedSample.certified_value}\n`;
      output += `Diff from Cert: ${selectedSample.diff_from_cert}\n`;
    }
    if (selectedSample.rpd_pct !== undefined) {
      output += `RPD %: ${selectedSample.rpd_pct}\n`;
      output += `Original Value: ${selectedSample.orig_value}\n`;
      output += `Duplicate Value: ${selectedSample.dup_value}\n`;
    }
    
    // Add surrounding samples if available
    if (surroundingSamples && surroundingSamples.length > 0) {
      output += '\n\n=== SURROUNDING SAMPLES ===\n\n';
      output += 'Sample ID\tDepth From\tDepth To\tSample Type\tControl Type\tAu (ppm)\tAg (ppm)\tCu (%)\n';
      surroundingSamples.forEach(s => {
        const marker = (s.sample_id === (selectedSample.sample_id || selectedSample.sample_number)) ? ' ⬅ Selected' : '';
        output += `${s.sample_id}${marker}\t${s.from_depth || '-'}\t${s.to_depth || '-'}\t${s.sample_type || '-'}\t${s.control_type || '-'}\t${s.au_best || '-'}\t${s.ag_best || '-'}\t${s.cu_best || '-'}\n`;
      });
    }
    
    // Copy to clipboard
    navigator.clipboard.writeText(output).then(() => {
      alert('Sample data copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy data to clipboard');
    });
  };

  const handleSaveNote = async (workorderId) => {
    try {
      setSavingNote(true);
      await qaqcService.saveWorkorderNote(workorderId, noteText);
      
      // Update local state
      setWorkorderNotes(prev => ({
        ...prev,
        [workorderId]: noteText
      }));
      
      setEditingNoteFor(null);
      setNoteText('');
      console.log(`✅ Note saved for workorder ${workorderId}`);
    } catch (err) {
      console.error('Failed to save note:', err);
      alert(`Failed to save note: ${err.message}`);
    } finally {
      setSavingNote(false);
    }
  };

  // Calculate summary statistics - optimized with pre-calculated summary endpoint
  const getSummaryStats = () => {
    // If we have the optimized summary, use it (fast path)
    if (dashboardSummary && loadedDatasets.has('dashboardSummary')) {
      return {
        totalFailures: dashboardSummary.unresolved_failures,
        unresolvedFailures: dashboardSummary.unresolved_failures,
        resolvedFailures: dashboardSummary.resolved_failures,
        activeAlerts: dashboardSummary.total_alerts,
        sampleMismatches: dashboardSummary.sample_mismatches,
        controlFailures: dashboardSummary.control_failures_from_master,
        duplicateFailures: dashboardSummary.duplicate_failures_from_master,
        blankAgFails: dashboardSummary.ag_failures,
        blankAuFails: dashboardSummary.au_failures,
        blankCuFails: dashboardSummary.cu_failures,
        totalBlankFailures: dashboardSummary.total_blank_failures
      };
    }
    
    // Fallback: Calculate from individual datasets (slow path, for detail tabs)
    const { 
      sampleCountReconcile, 
      controlStandards, 
      fieldDuplicates, 
      qaqcFailures
    } = dashboardData;
    
    // Critical metrics (always loaded for overview)
    const totalFailures = loadedDatasets.has('qaqcFailures') 
      ? qaqcFailures.filter(item => !item.resolved).length 
      : null;
    
    const unresolvedFailures = loadedDatasets.has('qaqcFailures')
      ? qaqcFailures.filter(item => !item.resolved).length
      : null;
      
    const resolvedFailures = loadedDatasets.has('qaqcFailures')
      ? qaqcFailures.filter(item => item.resolved).length
      : null;
    
    const activeAlerts = null;
    
    // Optional metrics (only calculated if data is loaded)
    const sampleMismatches = loadedDatasets.has('sampleCountReconcile')
      ? sampleCountReconcile.filter(item => !item.is_match).length
      : null;
    
    const controlFailures = loadedDatasets.has('controlStandards')
      ? controlStandards.filter(item => !item.within_3sd && item.certified_value).length
      : null;
    
    const duplicateFailures = loadedDatasets.has('fieldDuplicates')
      ? fieldDuplicates.filter(item =>
          !effectiveWithinRpdDynamic(item) || !item.within_rpd_tolerance_static
        ).length
      : null;
    
    const blankAgFails = null;
    const blankAuFails = null;
    const blankCuFails = null;
    const totalBlankFailures = null;
    
    return {
      totalFailures,
      unresolvedFailures,
      resolvedFailures,
      activeAlerts,
      sampleMismatches,
      controlFailures,
      duplicateFailures,
      blankAgFails,
      blankAuFails,
      blankCuFails,
      totalBlankFailures
    };
  };

  // Filter failures by resolved status
  const getFilteredFailures = () => {
    if (showResolved) {
      return dashboardData.qaqcFailures;
    }
    return dashboardData.qaqcFailures.filter(item => !item.resolved);
  };

  const handleFailureUpdated = (updated) => {
    if (updated?.id == null) return;
    const updatedId = String(updated.id);
    setDashboardData((prev) => ({
      ...prev,
      qaqcFailures: (prev.qaqcFailures || []).map((f) =>
        String(f.id) === updatedId ? { ...f, ...updated } : f
      ),
    }));
  };

  const handleViewFailureSample = (failure) => {
    const tab = mapFailureGroupToTab(failure?.failure_group);
    if (!tab) return;

    const sampleNumber = failure.sample_number || failure.dup_sample_number || '';
    const element = normalizeQaqcElement(failure.element);

    if (tab === 'controls') {
      if (element) setChartElement(element);
      setChartCRM('all');
    }
    if (tab === 'blanks-analysis' && element) setBlanksContamElement(element);
    if (tab === 'duplicates-precision' && element) setDuplicatesPrecisionElement(element);

    handleClearSelection();
    setPendingSampleNav({ tab, sampleNumber, element });
    setActiveTab(tab);
  };

  // Keep pending nav while on the destination tab (survives Strict Mode remounts).
  // Clear when the user leaves that tab.
  useEffect(() => {
    if (!pendingSampleNav) return;
    if (activeTab !== pendingSampleNav.tab) {
      setPendingSampleNav(null);
    }
  }, [activeTab, pendingSampleNav]);

  // Check if any datasets are currently loading
  const isLoading = Object.values(loadingStates).some(state => state === true);
  
  const stats = !error ? getSummaryStats() : null;
  const filteredFailures = !error ? getFilteredFailures() : [];

  return (
    <Layout user={user} currentRoute="/qaqc">
      <div className="qaqc">
        <div className="qaqc-header">
          <IconText icon={ICON.appQaqc} as="h1">QAQC Dashboard</IconText>
          <p>Quality assurance and quality control for drilling data</p>
        </div>

        {error && (
          <Card className="error-card">
            <IconText icon={ICON.uiError} as="h3">Error Loading Data</IconText>
            <p>{error}</p>
            <Button onClick={() => loadDataForTab(activeTab)}>Retry</Button>
          </Card>
        )}

        {!error && stats && (
          <>
            {/* Summary Card */}
            <Card className="summary-card">
              <IconText icon={ICON.uiChartAnalytics} as="h2">QAQC Overview</IconText>
              <div className="summary-grid">
                <div className="stat-item">
                  <span className="stat-label">Total Failures</span>
                  <span className={`stat-value ${stats.totalFailures !== null && stats.totalFailures > 0 ? 'status-warning' : 'status-pass'}`}>
                    {stats.totalFailures !== null ? stats.totalFailures : '-'}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Active Alerts</span>
                  <span className={`stat-value ${stats.activeAlerts !== null && stats.activeAlerts > 0 ? 'status-warning' : 'status-pass'}`}>
                    {stats.activeAlerts !== null ? stats.activeAlerts : '-'}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Sample Mismatches</span>
                  <span className={`stat-value ${stats.sampleMismatches !== null && stats.sampleMismatches > 0 ? 'status-warning' : 'status-pass'}`}>
                    {stats.sampleMismatches !== null ? stats.sampleMismatches : '-'}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Control Failures</span>
                  <span className={`stat-value ${stats.controlFailures !== null && stats.controlFailures > 0 ? 'status-warning' : 'status-pass'}`}>
                    {stats.controlFailures !== null ? stats.controlFailures : '-'}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Duplicate Failures</span>
                  <span className={`stat-value ${stats.duplicateFailures !== null && stats.duplicateFailures > 0 ? 'status-warning' : 'status-pass'}`}>
                    {stats.duplicateFailures !== null ? stats.duplicateFailures : '-'}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Unresolved Issues</span>
                  <span className={`stat-value ${stats.unresolvedFailures !== null && stats.unresolvedFailures > 0 ? 'status-fail' : 'status-pass'}`}>
                    {stats.unresolvedFailures !== null ? stats.unresolvedFailures : '-'}
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Resolved Issues</span>
                  <span className="stat-value status-pass">
                    {stats.resolvedFailures !== null ? stats.resolvedFailures : '-'}
                  </span>
                </div>
              </div>
            </Card>

            {/* Controls Card */}
            <Card className="controls-card">
              <div className="controls-row">
                <div className="tab-buttons" style={{ flexWrap: 'wrap' }}>
                  <Button 
                    onClick={() => setActiveTab('overview')} 
                    variant={activeTab === 'overview' ? 'primary' : 'secondary'}
                  >
                    Overview
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('standards-drift')} 
                    variant={activeTab === 'standards-drift' ? 'primary' : 'secondary'}
                  >
                    Standards Drift
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('standards-heatmap')} 
                    variant={activeTab === 'standards-heatmap' ? 'primary' : 'secondary'}
                  >
                    Standards Heatmap
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('standards-trend')} 
                    variant={activeTab === 'standards-trend' ? 'primary' : 'secondary'}
                  >
                    Standards Trend
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('failures')} 
                    variant={activeTab === 'failures' ? 'primary' : 'secondary'}
                  >
                    Failures ({stats.unresolvedFailures})
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('samples')} 
                    variant={activeTab === 'samples' ? 'primary' : 'secondary'}
                  >
                    Sample Reconcile
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('controls')} 
                    variant={activeTab === 'controls' ? 'primary' : 'secondary'}
                  >
                    Control Standards
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('duplicates')} 
                    variant={activeTab === 'duplicates' ? 'primary' : 'secondary'}
                  >
                    Field Duplicates
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('blanks-analysis')} 
                    variant={activeTab === 'blanks-analysis' ? 'primary' : 'secondary'}
                  >
                    Blanks Analysis
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('duplicates-precision')} 
                    variant={activeTab === 'duplicates-precision' ? 'primary' : 'secondary'}
                  >
                    Duplicates Precision
                  </Button>
                  <Button 
                    onClick={() => setActiveTab('reports')} 
                    variant={activeTab === 'reports' ? 'primary' : 'secondary'}
                  >
                    PDF Reports
                  </Button>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button 
                    onClick={handleRefreshFailures} 
                    variant="secondary"
                    disabled={refreshingFailures || isLoading}
                  >
                    {refreshingFailures ? (<><SelkirkIcon name={ICON.uiHourglass} size={16} decorative /> Refreshing...</>) : (<IconText icon={ICON.uiRefresh} iconSize={16}>Refresh Failures</IconText>)}
                  </Button>
                  <Button 
                    onClick={loadDashboardData} 
                    variant="secondary"
                    disabled={isLoading || refreshingFailures}
                  >
                    <IconText icon={ICON.uiRefresh} iconSize={16}>Refresh All</IconText>
                  </Button>
                </div>
              </div>
            </Card>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              loadingStates.dashboardSummary ? (
                <Card>
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading dashboard summary...</p>
                  </div>
                </Card>
              ) : (
                <OverviewTab data={dashboardData} stats={stats} />
              )
            )}

            {activeTab === 'failures' && (
              loadingStates.qaqcFailures ? (
                <Card>
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading failures data...</p>
                  </div>
                </Card>
              ) : (
                <FailuresTab 
                  data={filteredFailures} 
                  showResolved={showResolved}
                  setShowResolved={setShowResolved}
                  onFailureUpdated={handleFailureUpdated}
                  onViewSample={handleViewFailureSample}
                  failureDeepLink={failureDeepLink}
                  onFailureDeepLinkConsumed={() => setFailureDeepLink(null)}
                />
              )
            )}

            {activeTab === 'standards-drift' && (
              <Card>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    marginBottom: '1rem'
                  }}
                >
                  <div style={{ flex: '1 1 280px' }}>
                    <h3 style={{ marginBottom: '0.5rem' }}>Standards Drift Analysis</h3>
                    <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>
                      Monitor systematic drift in certified reference materials (CRMs) using EWMA, rolling statistics, and run-rule violations.
                    </p>
                  </div>
                  {!loadingStates.standardsDrift && (
                    <ChartImageDownloadButton
                      disabled={!dashboardData.standardsDrift?.length}
                      getJobs={() => [
                        {
                          Component: PrintStandardsDriftChart,
                          props: {
                            data: dashboardData.standardsDrift,
                            element: driftElement,
                            crmCode: driftCRM
                          },
                          filenameStem: `qaqc_standards_drift_${driftElement}_${driftCRM}`
                        }
                      ]}
                    />
                  )}
                </div>
                {loadingStates.standardsDrift ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading standards drift data...</p>
                  </div>
                ) : (
                  <StandardsDriftChart
                    data={dashboardData.standardsDrift}
                    selectedElement={driftElement}
                    selectedCRM={driftCRM}
                    onElementChange={setDriftElement}
                    onCRMChange={setDriftCRM}
                    onPointClick={(sample) => {
                      console.log('📍 Selected sample:', sample);
                    }}
                  />
                )}
              </Card>
            )}

            {activeTab === 'standards-heatmap' && (
              <Card>
                <h3 style={{ marginBottom: '1rem' }}>Standards Bias Heatmap</h3>
                <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '1rem' }}>
                  Visualize systematic bias patterns across workorders and elements/CRMs. Click cells to drill into drift charts.
                </p>
                {loadingStates.standardsBiasWorkorder ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading standards heatmap data...</p>
                  </div>
                ) : (
                  <StandardsHeatmapChart
                    data={dashboardData.standardsBiasWorkorder}
                    selectedElement={heatmapElement}
                    onElementChange={setHeatmapElement}
                    onCellClick={(cellData) => {
                      console.log('Cell clicked:', cellData);
                      // Navigate to drift chart with filters
                      setDriftElement(cellData.element);
                      setDriftCRM(cellData.crmCode);
                      setActiveTab('standards-drift');
                    }}
                  />
                )}
              </Card>
            )}

            {activeTab === 'standards-trend' && (
              <Card>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    marginBottom: '1rem'
                  }}
                >
                  <div style={{ flex: '1 1 280px' }}>
                    <h3 style={{ marginBottom: '0.5rem' }}>Standards Long-Term Trend</h3>
                    <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>
                      Track monthly bias trends to identify step changes or gradual drift in lab performance.
                    </p>
                  </div>
                  {!loadingStates.standardsBiasMonth && (
                    <ChartImageDownloadButton
                      disabled={!dashboardData.standardsBiasMonth?.length}
                      label={
                        trendElement === 'all'
                          ? 'Download chart images — Au, Ag, Cu (print style)'
                          : 'Download chart image (print style)'
                      }
                      getJobs={() => {
                        const elements = trendElement === 'all' ? ['Au', 'Ag', 'Cu'] : [trendElement];
                        return elements.map((el) => ({
                          Component: PrintStandardsTrendChart,
                          props: {
                            data: dashboardData.standardsBiasMonth,
                            element: el,
                            crmCode: trendCRM
                          },
                          filenameStem: `qaqc_standards_trend_${el}_${trendCRM}`
                        }));
                      }}
                    />
                  )}
                </div>
                {loadingStates.standardsBiasMonth ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading standards trend data...</p>
                  </div>
                ) : (
                  <StandardsTrendChart
                    data={dashboardData.standardsBiasMonth}
                    selectedElement={trendElement}
                    selectedCRM={trendCRM}
                    onElementChange={setTrendElement}
                    onCRMChange={setTrendCRM}
                  />
                )}
              </Card>
            )}

            {activeTab === 'blanks-analysis' && (
              <Card>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    marginBottom: '1rem'
                  }}
                >
                  <div style={{ flex: '1 1 280px' }}>
                    <h3 style={{ marginBottom: '0.5rem' }}>Blanks Contamination Analysis</h3>
                    <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>
                      Detect contamination events and carryover effects in blank samples with enhanced drift detection.
                    </p>
                  </div>
                  {!loadingStates.blanksDrift && (
                    <ChartImageDownloadButton
                      disabled={!dashboardData.blanksDrift?.length}
                      getJobs={() => [
                        {
                          Component: PrintBlanksChart,
                          props: {
                            data: dashboardData.blanksDrift,
                            element: blanksContamElement
                          },
                          filenameStem: `qaqc_blanks_${blanksContamElement}`
                        }
                      ]}
                    />
                  )}
                </div>
                {loadingStates.blanksDrift ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading blanks analysis data...</p>
                  </div>
                ) : (
                  <BlanksContaminationChart
                    data={dashboardData.blanksDrift}
                    selectedElement={blanksContamElement}
                    selectedSample={selectedBlank}
                    surroundingSamples={surroundingSamples}
                    loadingSurrounding={loadingSurrounding}
                    onElementChange={setBlanksContamElement}
                    onPointClick={(blank) => handleSampleClick(blank, 'blank')}
                    onClearSelection={handleClearSelection}
                    onCopyData={() => handleCopyData(selectedBlank, surroundingSamples)}
                    externalSampleFilter={
                      pendingSampleNav?.tab === 'blanks-analysis'
                        ? pendingSampleNav.sampleNumber
                        : null
                    }
                  />
                )}
              </Card>
            )}

            {activeTab === 'duplicates-precision' && (
              <Card>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '1rem',
                    flexWrap: 'wrap',
                    marginBottom: '1rem'
                  }}
                >
                  <div style={{ flex: '1 1 280px' }}>
                    <h3 style={{ marginBottom: '0.5rem' }}>Duplicates Precision Analysis</h3>
                    <p style={{ color: '#9ca3af', fontSize: '0.875rem', margin: 0 }}>
                      Monitor precision trends using RPD analysis with rolling statistics and dynamic tolerances.
                    </p>
                  </div>
                  {!loadingStates.duplicatesDrift && (
                    <ChartImageDownloadButton
                      disabled={!dashboardData.duplicatesDrift?.length}
                      label="Download RPD trend chart (print style)"
                      getJobs={() => [
                        {
                          Component: PrintDuplicatesChart,
                          props: {
                            data: dashboardData.duplicatesDrift,
                            element: duplicatesPrecisionElement
                          },
                          filenameStem: `qaqc_duplicates_rpd_${duplicatesPrecisionElement}`
                        }
                      ]}
                    />
                  )}
                </div>
                {loadingStates.duplicatesDrift ? (
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading duplicates precision data...</p>
                  </div>
                ) : (
                  <DuplicatesPrecisionChart
                    data={dashboardData.duplicatesDrift}
                    selectedElement={duplicatesPrecisionElement}
                    selectedSample={selectedDuplicate}
                    surroundingSamples={surroundingSamples}
                    loadingSurrounding={loadingSurrounding}
                    onElementChange={setDuplicatesPrecisionElement}
                    onPointClick={(duplicate) => handleSampleClick(duplicate, 'duplicate')}
                    onClearSelection={handleClearSelection}
                    onCopyData={() => handleCopyData(selectedDuplicate, surroundingSamples)}
                    externalSampleFilter={
                      pendingSampleNav?.tab === 'duplicates-precision'
                        ? pendingSampleNav.sampleNumber
                        : null
                    }
                  />
                )}
              </Card>
            )}

            {activeTab === 'reports' && (
              <Card>
                <QAQCReportsTab 
                  dashboardData={dashboardData}
                  dashboardSummary={dashboardSummary}
                  loadingStates={loadingStates}
                />
              </Card>
            )}

            {activeTab === 'samples' && (
              (loadingStates.sampleCountReconcile || loadingStates.qaqcFailures) ? (
                <Card>
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading sample reconciliation data...</p>
                  </div>
                </Card>
              ) : (
                <SampleReconcileTab 
                  data={dashboardData.sampleCountReconcile} 
                  qaqcFailures={dashboardData.qaqcFailures}
                  workorderNotes={workorderNotes}
                  editingNoteFor={editingNoteFor}
                  noteText={noteText}
                  savingNote={savingNote}
                  onEditNote={(workorderId) => {
                    setEditingNoteFor(workorderId);
                    setNoteText(workorderNotes[workorderId] || '');
                  }}
                  onCancelEdit={() => {
                    setEditingNoteFor(null);
                    setNoteText('');
                  }}
                  onNoteTextChange={setNoteText}
                  onSaveNote={handleSaveNote}
                />
              )
            )}

            {activeTab === 'controls' && (
              loadingStates.controlStandards ? (
                <Card>
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading control standards data...</p>
                  </div>
                </Card>
              ) : (
                <ControlStandardsTab 
                  data={dashboardData.controlStandards}
                  chartElement={chartElement}
                  chartCRM={chartCRM}
                  selectedSample={selectedSample}
                  surroundingSamples={surroundingSamples}
                  loadingSurrounding={loadingSurrounding}
                  onElementChange={setChartElement}
                  onCRMChange={setChartCRM}
                  onPointClick={(sample) => handleSampleClick(sample, 'standard')}
                  onClearSelection={handleClearSelection}
                  onCopyData={() => handleCopyData(selectedSample, surroundingSamples)}
                  externalSampleFilter={
                    pendingSampleNav?.tab === 'controls'
                      ? pendingSampleNav.sampleNumber
                      : null
                  }
                />
              )
            )}

            {activeTab === 'duplicates' && (
              loadingStates.fieldDuplicates ? (
                <Card>
                  <div style={{ textAlign: 'center', padding: '3rem' }}>
                    <Spinner />
                    <p style={{ marginTop: '1rem', color: '#9ca3af' }}>Loading field duplicates data...</p>
                  </div>
                </Card>
              ) : (
                <FieldDuplicatesTab data={dashboardData.fieldDuplicates} />
              )
            )}

          </>
        )}
      </div>
    </Layout>
  );
};

// ============================================================================
// TAB COMPONENTS
// ============================================================================

const OverviewTab = ({ data, stats }) => {
  return (
    <div className="overview-tab">
      <Card className="content-card">
        <IconText icon={ICON.uiChartAnalytics} as="h2">QAQC Summary</IconText>
        <div className="overview-sections">
          <div className="overview-section">
            <IconText icon={ICON.uiNumbers} as="h3">Sample Count Reconciliation</IconText>
            <p>Total workorders: {data.sampleCountReconcile.length}</p>
            <p>Mismatches: <span className={stats.sampleMismatches > 0 ? 'text-warning' : 'text-pass'}>{stats.sampleMismatches}</span></p>
          </div>
          
          <div className="overview-section">
            <IconText icon={ICON.uiFilter} as="h3">Control Standards</IconText>
            <p>Total samples: {data.controlStandards.length}</p>
            <p>Outside 3SD: <span className={stats.controlFailures > 0 ? 'text-warning' : 'text-pass'}>{stats.controlFailures}</span></p>
          </div>
          
          <div className="overview-section">
            <IconText icon={ICON.qaqcDomainDuplicate} as="h3">Field Duplicates</IconText>
            <p>Total pairs: {data.fieldDuplicates.length}</p>
            <p>Tolerance failures: <span className={stats.duplicateFailures > 0 ? 'text-warning' : 'text-pass'}>{stats.duplicateFailures}</span></p>
          </div>
          
          <div className="overview-section">
            <IconText icon={ICON.uiInvestigation} as="h3">Investigation Status</IconText>
            <p>Unresolved: <span className={stats.unresolvedFailures > 0 ? 'text-fail' : 'text-pass'}>{stats.unresolvedFailures}</span></p>
            <p>Resolved: <span className="text-pass">{stats.resolvedFailures}</span></p>
            <p>Total: {stats.totalFailures}</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

const FailuresTab = ({
  data,
  showResolved,
  setShowResolved,
  onFailureUpdated,
  onViewSample,
  failureDeepLink,
  onFailureDeepLinkConsumed,
}) => {
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedFailure, setSelectedFailure] = useState(null);
  const [editResolved, setEditResolved] = useState(false);
  const [editComments, setEditComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deepLinkApplied, setDeepLinkApplied] = useState(false);

  useEffect(() => {
    if (!failureDeepLink || deepLinkApplied || !data?.length) return;

    if (failureDeepLink.sampleNumber) {
      setSearchFilter(String(failureDeepLink.sampleNumber));
    } else if (failureDeepLink.holeId) {
      setSearchFilter(String(failureDeepLink.holeId));
    }

    if (failureDeepLink.failureId != null && failureDeepLink.failureId !== '') {
      const match = data.find(
        (item) => String(item.id) === String(failureDeepLink.failureId)
      );
      if (match) {
        setSelectedFailure(match);
        setEditResolved(Boolean(match.resolved));
        setEditComments(match.resolution_comments || '');
        setSaveError(null);
      }
    }

    setDeepLinkApplied(true);
    onFailureDeepLinkConsumed?.();
  }, [
    data,
    deepLinkApplied,
    failureDeepLink,
    onFailureDeepLinkConsumed,
  ]);
  
  // Filter data by search term
  const filteredData = data.filter(item => {
    if (!searchFilter) return true;
    const term = searchFilter.toLowerCase();
    return (
      item.sample_number?.toLowerCase().includes(term) ||
      item.workorder_id?.toLowerCase().includes(term) ||
      item.hole_id?.toLowerCase().includes(term) ||
      item.dup_sample_number?.toLowerCase().includes(term)
    );
  });

  const openFailureModal = (item) => {
    setSelectedFailure(item);
    setEditResolved(Boolean(item.resolved));
    setEditComments(item.resolution_comments || '');
    setSaveError(null);
  };

  const closeFailureModal = () => {
    setSelectedFailure(null);
    setSaveError(null);
    setSaving(false);
  };

  const handleSaveResolution = async () => {
    if (!selectedFailure?.id) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await qaqcService.updateFailureResolution({
        id: selectedFailure.id,
        resolved: editResolved,
        resolutionComments: editComments,
      });
      const merged = { ...selectedFailure, ...updated, resolved: editResolved, resolution_comments: editComments };
      onFailureUpdated?.(merged);
      setSelectedFailure(merged);
    } catch (err) {
      setSaveError(err.message || 'Failed to save resolution');
    } finally {
      setSaving(false);
    }
  };

  const canViewSample = selectedFailure && mapFailureGroupToTab(selectedFailure.failure_group);
  
  return (
    <div className="failures-tab">
      <Card className="content-card">
        <div className="card-header-row">
          <IconText icon={ICON.uiInvestigation} as="h2">QAQC Failures & Investigations</IconText>
          <div className="filter-controls" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1 }}>
              <label style={{ fontSize: '0.875rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>Search:</label>
              <input
                type="text"
                placeholder="Filter by sample, workorder, or hole ID..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                style={{
                  padding: '0.375rem 0.75rem',
                  background: '#374151',
                  color: '#f9fafb',
                  border: '1px solid #4b5563',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  flex: 1,
                  minWidth: '250px'
                }}
              />
              {searchFilter && (
                <IconButton
                  icon={ICON.ddClose}
                  onClick={() => setSearchFilter('')}
                  ariaLabel="Clear filter"
                />
              )}
            </div>
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={showResolved}
                onChange={(e) => setShowResolved(e.target.checked)}
              />
              Show Resolved
            </label>
          </div>
        </div>
        
        {filteredData.length === 0 ? (
          <div className="no-data">
            <div className="no-data-icon"><SelkirkIcon name={searchFilter ? ICON.uiSearch : ICON.uiSuccess} size={48} decorative /></div>
            <h3>{searchFilter ? 'No Matches Found' : showResolved ? 'No Failures Found' : 'No Unresolved Failures'}</h3>
            <p>
              {searchFilter 
                ? `No failures match "${searchFilter}". Try a different search term.`
                : 'All quality control checks are passing.'}
            </p>
          </div>
        ) : (
          <>
            {searchFilter && (
              <p style={{ 
                color: '#9ca3af', 
                fontSize: '0.875rem', 
                marginBottom: '1rem',
                fontStyle: 'italic'
              }}>
                Showing {filteredData.length} of {data.length} failures
              </p>
            )}
            <div className="table-container">
              <table className="qaqc-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Workorder</th>
                    <th>Sample</th>
                    <th>Hole ID</th>
                    <th>Element</th>
                    <th>Failure</th>
                    <th>Status</th>
                    <th>Investigation</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item) => (
                  <tr
                    key={item.id ?? `${item.failure_key}-${item.sample_number}-${item.element}`}
                    className={`clickable-failure-row ${item.resolved ? 'resolved' : 'unresolved'}`}
                    onClick={() => openFailureModal(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openFailureModal(item);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    title="View failure details"
                  >
                    <td className="failure-group">{item.failure_group}</td>
                    <td>{item.workorder_id || '-'}</td>
                    <td>{item.sample_number || '-'}</td>
                    <td>{item.hole_id}</td>
                    <td>{item.element}</td>
                    <td className="failure-type">{item.failure_type}</td>
                    <td>
                      <span className={`status-badge ${item.resolved ? 'status-resolved' : 'status-open'}`}>
                        {item.resolved ? (<IconText icon={ICON.uiCheckOn} iconSize={14}>Resolved</IconText>) : (<IconText icon={ICON.uiWarning} iconSize={14}>Open</IconText>)}
                      </span>
                    </td>
                    <td className="resolution-comments">
                      {item.resolution_comments || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Card>

      {selectedFailure && (
        <div className="modal-overlay" onClick={closeFailureModal}>
          <div className="modal-content failure-detail-modal" onClick={(e) => e.stopPropagation()}>
            <Card className="hole-detail-card">
              <div className="modal-header">
                <h2>Failure Details</h2>
                <button type="button" className="close-button" onClick={closeFailureModal} aria-label="Close">
                  ×
                </button>
              </div>

              <div className="failure-detail-grid">
                <div className="failure-detail-item">
                  <span className="failure-detail-label">Type</span>
                  <span>{selectedFailure.failure_group || '-'}</span>
                </div>
                <div className="failure-detail-item">
                  <span className="failure-detail-label">Workorder</span>
                  <span>{selectedFailure.workorder_id || '-'}</span>
                </div>
                <div className="failure-detail-item">
                  <span className="failure-detail-label">Sample</span>
                  <span>{selectedFailure.sample_number || '-'}</span>
                </div>
                <div className="failure-detail-item">
                  <span className="failure-detail-label">Hole ID</span>
                  <span>{selectedFailure.hole_id || '-'}</span>
                </div>
                <div className="failure-detail-item">
                  <span className="failure-detail-label">Element</span>
                  <span>{selectedFailure.element || '-'}</span>
                </div>
                <div className="failure-detail-item">
                  <span className="failure-detail-label">Failure</span>
                  <span>{selectedFailure.failure_type || '-'}</span>
                </div>
                {selectedFailure.z_score != null && selectedFailure.z_score !== '' && (
                  <div className="failure-detail-item">
                    <span className="failure-detail-label">Z-score</span>
                    <span>{Number(selectedFailure.z_score).toFixed(3)}</span>
                  </div>
                )}
                {selectedFailure.value != null && selectedFailure.value !== '' && (
                  <div className="failure-detail-item">
                    <span className="failure-detail-label">Value</span>
                    <span>{selectedFailure.value}</span>
                  </div>
                )}
                {selectedFailure.certified_value != null && selectedFailure.certified_value !== '' && (
                  <div className="failure-detail-item">
                    <span className="failure-detail-label">Certified</span>
                    <span>{selectedFailure.certified_value}</span>
                  </div>
                )}
                {selectedFailure.rpd != null && selectedFailure.rpd !== '' && (
                  <div className="failure-detail-item">
                    <span className="failure-detail-label">RPD</span>
                    <span>{selectedFailure.rpd}</span>
                  </div>
                )}
                {selectedFailure.dup_sample_number && (
                  <div className="failure-detail-item">
                    <span className="failure-detail-label">Dup Sample</span>
                    <span>{selectedFailure.dup_sample_number}</span>
                  </div>
                )}
              </div>

              {(selectedFailure.investigation_id || selectedFailure.failure_view) && (
                <p className="failure-detail-meta">
                  {selectedFailure.failure_view && <span>View: {selectedFailure.failure_view}</span>}
                  {selectedFailure.investigation_id && (
                    <span>Investigation: {selectedFailure.investigation_id}</span>
                  )}
                </p>
              )}

              <div className="failure-detail-form">
                <label className="checkbox-label failure-resolved-check">
                  <input
                    type="checkbox"
                    checked={editResolved}
                    onChange={(e) => setEditResolved(e.target.checked)}
                  />
                  Resolved
                </label>
                <label className="failure-detail-label" htmlFor="failure-resolution-comments">
                  Resolution comments
                </label>
                <textarea
                  id="failure-resolution-comments"
                  className="failure-resolution-textarea"
                  rows={4}
                  value={editComments}
                  onChange={(e) => setEditComments(e.target.value)}
                  placeholder="Investigation notes and resolution details..."
                />
                {saveError && <p className="failure-save-error">{saveError}</p>}
              </div>

              <div className="failure-detail-actions">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!canViewSample}
                  onClick={() => {
                    const failure = selectedFailure;
                    closeFailureModal();
                    onViewSample?.(failure);
                  }}
                >
                  View sample
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  disabled={saving || !selectedFailure?.id}
                  onClick={handleSaveResolution}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

const SampleReconcileTab = ({ 
  data, 
  qaqcFailures = [],
  workorderNotes = {},
  editingNoteFor = null,
  noteText = '',
  savingNote = false,
  onEditNote,
  onCancelEdit,
  onNoteTextChange,
  onSaveNote
}) => {
  const mismatches = data.filter(item => !item.is_match);
  
  // Create a Set of workorder_ids that have unresolved failures
  const workordersWithFailures = new Set(
    qaqcFailures
      .filter(failure => !failure.resolved)
      .map(failure => failure.workorder_id)
  );
  
  // Helper function to check if a workorder has unresolved failures
  const hasUnresolvedFailures = (workorderId) => {
    return workordersWithFailures.has(workorderId);
  };
  
  // Exception notes are now managed via the editable notes system
  // (removed hardcoded workorderExceptions)
  
  return (
    <div className="sample-reconcile-tab">
      <Card className="content-card">
        <IconText icon={ICON.uiNumbers} as="h2">Sample Count Reconciliation</IconText>
        <p className="card-description">
          Comparing expected vs actual sample counts for <strong>Finalized</strong> workorders
          {qaqcFailures.length > 0 && (
            <span style={{ marginLeft: '1rem', color: '#f59e0b' }}>
              <IconText icon={ICON.uiWarning} iconSize={14} as="span">Rows highlighted in orange</IconText> have unresolved QAQC failures
            </span>
          )}
        </p>
        
        {mismatches.length === 0 ? (
          <div className="no-data">
            <div className="no-data-icon"><SelkirkIcon name={ICON.uiSuccess} size={48} decorative /></div>
            <h3>All Samples Match</h3>
            <p>No discrepancies found between expected and actual sample counts.</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="qaqc-table">
              <thead>
                <tr>
                  <th>Workorder ID</th>
                  <th>Status</th>
                  <th>Expected</th>
                  <th>Actual</th>
                  <th>Difference</th>
                  <th>Match</th>
                  <th>QAQC Status</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {mismatches.map((item, index) => {
                  return (
                    <tr 
                      key={index}
                      className={hasUnresolvedFailures(item.workorder_id) ? 'row-with-failures' : ''}
                    >
                      <td>{item.workorder_id}</td>
                      <td>{item.status}</td>
                      <td>{item.expected_count}</td>
                      <td>{item.actual_count}</td>
                      <td className={item.diff < 0 ? 'text-fail' : 'text-warning'}>
                        {item.diff}
                      </td>
                      <td>
                        <span className={`status-badge ${item.is_match ? 'status-pass' : 'status-fail'}`}>
                          {item.is_match ? <SelkirkIcon name={ICON.uiCheckOn} size={16} decorative /> : <SelkirkIcon name={ICON.uiError} size={16} decorative />}
                        </span>
                      </td>
                      <td>
                        {hasUnresolvedFailures(item.workorder_id) ? (
                          <span className="status-badge status-warning">
                            <IconText icon={ICON.uiWarning} iconSize={14}>Has Failures</IconText>
                          </span>
                        ) : (
                          <span className="status-badge status-pass">
                            <IconText icon={ICON.uiCheckOn} iconSize={14}>Clean</IconText>
                          </span>
                        )}
                      </td>
                      <td>
                        {/* Editable notes section */}
                        {editingNoteFor === item.workorder_id ? (
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <input
                              type="text"
                              value={noteText}
                              onChange={(e) => onNoteTextChange(e.target.value)}
                              placeholder="Add note..."
                              autoFocus
                              style={{
                                flex: 1,
                                padding: '0.25rem 0.5rem',
                                background: '#374151',
                                color: '#f9fafb',
                                border: '1px solid #4b5563',
                                borderRadius: '4px',
                                fontSize: '0.875rem'
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  onSaveNote(item.workorder_id);
                                }
                              }}
                            />
                            <button 
                              onClick={() => onSaveNote(item.workorder_id)}
                              disabled={savingNote}
                              title="Save note"
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#10b981',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                cursor: savingNote ? 'wait' : 'pointer',
                                minWidth: '2rem'
                              }}
                            >
                              {savingNote ? '...' : <SelkirkIcon name={ICON.uiCheckOn} size={14} decorative />}
                            </button>
                            <button
                              type="button"
                              onClick={onCancelEdit}
                              title="Cancel"
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#374151',
                                color: '#f9fafb',
                                border: '1px solid #4b5563',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                              }}
                            >
                              <SelkirkIcon name={ICON.uiClose} size={14} decorative />
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ 
                              fontSize: '0.875rem', 
                              color: workorderNotes[item.workorder_id] ? '#f9fafb' : '#9ca3af', 
                              fontStyle: workorderNotes[item.workorder_id] ? 'normal' : 'italic',
                              flex: 1
                            }}>
                              {workorderNotes[item.workorder_id] || 'No notes'}
                            </span>
                            <button 
                              onClick={() => onEditNote(item.workorder_id)}
                              title={workorderNotes[item.workorder_id] ? 'Edit note' : 'Add note'}
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: 'transparent',
                                color: '#06b6d4',
                                border: '1px solid #06b6d4',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {workorderNotes[item.workorder_id] ? (<IconText icon={ICON.uiEdit} iconSize={14}>Edit</IconText>) : (<IconText icon={ICON.uiAdd} iconSize={14}>Add</IconText>)}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

const ControlStandardsTab = ({ data, chartElement, chartCRM, onElementChange, onCRMChange, onPointClick, selectedSample, onClearSelection, surroundingSamples, loadingSurrounding, onCopyData, externalSampleFilter = null }) => {
  const failures = data.filter(item => item.certified_value && !item.within_3sd);
  
  // Filter table data based on selected sample
  const filteredData = selectedSample 
    ? data.filter(item => item.sample_id === selectedSample.sample_id || item.sample_number === selectedSample.sample_number)
    : data;
  
  return (
    <div className="control-standards-tab">
      {/* Interactive Chart */}
      <Card className="content-card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
          <ChartImageDownloadButton
            disabled={!data?.length}
            getJobs={() => [
              {
                Component: PrintControlStandardsChart,
                props: {
                  data,
                  element: chartElement,
                  crmCode: chartCRM
                },
                filenameStem: `qaqc_control_standards_${chartElement}_${chartCRM}`
              }
            ]}
          />
        </div>
        <ControlStandardsChart
          data={data}
          selectedElement={chartElement}
          selectedCRM={chartCRM}
          onElementChange={onElementChange}
          onCRMChange={onCRMChange}
          onPointClick={onPointClick}
          externalSampleFilter={externalSampleFilter}
          height={450}
        />
      </Card>
      
      {/* Data Table */}
      <Card className="content-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <IconText icon={ICON.uiFilter} as="h2">Control Standards Data Table</IconText>
            <p className="card-description">
              Control standards (blanks & CRMs) with z-score analysis
            </p>
          </div>
          {selectedSample && (
            <Button onClick={onClearSelection} variant="secondary">
              <IconText icon={ICON.uiClose} iconSize={16}>Clear Filter</IconText>
            </Button>
          )}
        </div>
        
        {selectedSample && (
          <div style={{ 
            padding: '1.5rem',
            background: '#111827',
            border: '2px solid #06b6d4',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '1rem'
            }}>
              <h4 style={{ margin: 0, color: '#06b6d4' }}>
                Selected Sample Details: {selectedSample.sample_number}
              </h4>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                  onClick={onCopyData}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#06b6d4',
                    color: '#f9fafb',
                    border: '1px solid #0891b2',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  <IconText icon={ICON.uiClipboardList} iconSize={16}>Copy Data</IconText>
                </button>
                <button 
                  onClick={onClearSelection}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#374151',
                    color: '#f9fafb',
                    border: '1px solid #4b5563',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  <IconText icon={ICON.uiClose} iconSize={16}>Clear Selection</IconText>
                </button>
              </div>
            </div>
            
            <table className="sample-details-table">
              <tbody>
                <tr>
                  <th>Workorder ID</th>
                  <td>{selectedSample.workorder_id}</td>
                  <th>CRM Code</th>
                  <td>{selectedSample.crm_code}</td>
                </tr>
                <tr>
                  <th>Element</th>
                  <td>{selectedSample.element}</td>
                  <th>Hole ID</th>
                  <td>{selectedSample.hole_id || 'N/A'}</td>
                </tr>
                <tr>
                  <th>Best Value</th>
                  <td>{parseFloat(selectedSample.best_value || selectedSample.value).toFixed(5)}</td>
                  <th>Certified Value</th>
                  <td>{parseFloat(selectedSample.certified_value).toFixed(5)}</td>
                </tr>
                <tr>
                  <th>Diff from Cert</th>
                  <td>{parseFloat(selectedSample.diff_from_cert).toFixed(5)}</td>
                  <th>Z-Score</th>
                  <td style={{ 
                    color: Math.abs(parseFloat(selectedSample.z_score)) > 3 ? '#ef4444' : '#f9fafb'
                  }}>
                    {parseFloat(selectedSample.z_score).toFixed(3)}
                  </td>
                </tr>
                <tr>
                  <th>Within 2SD</th>
                  <td style={{ color: selectedSample.within_2sd ? '#10b981' : '#f59e0b' }}>
                    {selectedSample.within_2sd ? 'YES' : 'NO'}
                  </td>
                  <th>Within 3SD</th>
                  <td style={{ color: selectedSample.within_3sd ? '#10b981' : '#ef4444' }}>
                    {selectedSample.within_3sd ? 'YES' : 'NO'}
                  </td>
                </tr>
                <tr>
                  <th>Received Date</th>
                  <td>{selectedSample.received_date ? new Date(selectedSample.received_date).toLocaleString() : 'N/A'}</td>
                  <th>Lab Code</th>
                  <td>{selectedSample.lab_code || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
            
            <SurroundingSamplesTable 
              data={surroundingSamples}
              loading={loadingSurrounding}
              selectedSample={selectedSample}
            />
          </div>
        )}
        
        {filteredData.length === 0 ? (
          <div className="no-data">
            <div className="no-data-icon"><SelkirkIcon name={ICON.qaqcDomainControlStandard} size={48} decorative /></div>
            <h3>No Control Standards Data</h3>
            <p>Control standards data will appear here once available.</p>
          </div>
        ) : (
          <>
            {failures.length > 0 && !selectedSample && (
              <div className="alert alert-warning">
                <strong><SelkirkIcon name={ICON.uiWarning} size={14} decorative /> {failures.length} control standards outside 3SD limits</strong>
              </div>
            )}
            <div className="table-container">
              <table className="qaqc-table">
                <thead>
                  <tr>
                    <th>Sample</th>
                    <th>Workorder</th>
                    <th>Hole ID</th>
                    <th>CRM Code</th>
                    <th>Element</th>
                    <th>Best Value</th>
                    <th>Certified</th>
                    <th>Diff</th>
                    <th>Z-Score</th>
                    <th>2SD</th>
                    <th>3SD</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.slice(0, 100).map((item, index) => {
                    // Helper function to safely format numbers
                    const formatNumber = (value, decimals) => {
                      const num = parseFloat(value);
                      return !isNaN(num) && num !== null ? num.toFixed(decimals) : '-';
                    };
                    
                    const isSelected = selectedSample && 
                      (item.sample_id === selectedSample.sample_id || item.sample_number === selectedSample.sample_number);
                    
                    return (
                      <tr 
                        key={index} 
                        className={`${item.certified_value && !item.within_3sd ? 'failure-row' : ''} ${isSelected ? 'selected-row' : ''}`}
                        style={isSelected ? { background: 'rgba(6, 182, 212, 0.15)' } : {}}
                      >
                        <td>{item.sample_number || '-'}</td>
                        <td>{item.workorder_id || '-'}</td>
                        <td>{item.hole_id || '-'}</td>
                        <td>{item.crm_code || '-'}</td>
                        <td>{item.element || '-'}</td>
                        <td>{item.best_value || '-'}</td>
                        <td>{formatNumber(item.certified_value, 4)}</td>
                        <td className={Math.abs(parseFloat(item.diff_from_cert) || 0) > 0.1 ? 'text-warning' : ''}>
                          {formatNumber(item.diff_from_cert, 4)}
                        </td>
                        <td className={Math.abs(parseFloat(item.z_score) || 0) > 3 ? 'text-fail' : Math.abs(parseFloat(item.z_score) || 0) > 2 ? 'text-warning' : ''}>
                          {formatNumber(item.z_score, 2)}
                        </td>
                        <td>
                          <span className={`status-badge ${item.within_2sd ? 'status-pass' : 'status-warning'}`}>
                            {item.within_2sd ? <SelkirkIcon name={ICON.uiCheckOn} size={16} decorative /> : <SelkirkIcon name={ICON.uiError} size={16} decorative />}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${item.within_3sd ? 'status-pass' : 'status-fail'}`}>
                            {item.within_3sd ? <SelkirkIcon name={ICON.uiCheckOn} size={16} decorative /> : <SelkirkIcon name={ICON.uiError} size={16} decorative />}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

const FieldDuplicatesTab = ({ data }) => {
  const failures = data.filter(item => !effectiveWithinRpdDynamic(item));
  
  return (
    <div className="field-duplicates-tab">
      <Card className="content-card">
        <IconText icon={ICON.qaqcDomainDuplicate} as="h2">Field Duplicates QC</IconText>
        <p className="card-description">
          Field duplicate analysis with RPD and dynamic tolerance checks
        </p>
        
        {data.length === 0 ? (
          <div className="no-data">
            <div className="no-data-icon"><SelkirkIcon name={ICON.qaqcDomainDuplicate} size={48} decorative /></div>
            <h3>No Field Duplicates Data</h3>
            <p>Field duplicate data will appear here once available.</p>
          </div>
        ) : (
          <>
            {failures.length > 0 && (
              <div className="alert alert-warning">
                <strong><SelkirkIcon name={ICON.uiWarning} size={14} decorative /> {failures.length} field duplicates outside dynamic tolerance</strong>
              </div>
            )}
            <div className="table-container">
              <table className="qaqc-table">
                <thead>
                  <tr>
                    <th>Parent</th>
                    <th>Orig/Dup</th>
                    <th>Workorder</th>
                    <th>Hole ID</th>
                    <th>Depth</th>
                    <th>Element</th>
                    <th>Orig Value</th>
                    <th>Dup Value</th>
                    <th>RPD %</th>
                    <th>Grade Bin</th>
                    <th>Static</th>
                    <th>Dynamic</th>
                  </tr>
                </thead>
                <tbody>
                  {data.slice(0, 100).map((item, index) => {
                    // Helper function to safely format numbers
                    const formatNumber = (value, decimals) => {
                      const num = parseFloat(value);
                      return !isNaN(num) && num !== null ? num.toFixed(decimals) : '-';
                    };
                    
                    return (
                      <tr key={index} className={!effectiveWithinRpdDynamic(item) ? 'failure-row' : ''}>
                        <td>{item.parent_sample || '-'}</td>
                        <td className="sample-pair">
                          {item.orig_sample_number || '-'}<br/>
                          {item.dup_sample_number || '-'}
                        </td>
                        <td>{item.orig_workorder_id || '-'}</td>
                        <td>{item.hole_id || '-'}</td>
                        <td>{item.from_depth || '-'}-{item.to_depth || '-'}</td>
                        <td>{item.element || '-'}</td>
                        <td>{item.orig_value || '-'}</td>
                        <td>{item.dup_value || '-'}</td>
                        <td className={parseFloat(item.rpd_pct) > 30 ? 'text-warning' : ''}>
                          {formatNumber(item.rpd_pct, 1)}%
                        </td>
                        <td>{item.grade_bin || '-'}</td>
                        <td>
                          <span className={`status-badge ${item.within_rpd_tolerance_static ? 'status-pass' : 'status-warning'}`}>
                            {item.within_rpd_tolerance_static ? <SelkirkIcon name={ICON.uiCheckOn} size={16} decorative /> : <SelkirkIcon name={ICON.uiError} size={16} decorative />}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${effectiveWithinRpdDynamic(item) ? 'status-pass' : 'status-fail'}`}>
                            {effectiveWithinRpdDynamic(item) ? <SelkirkIcon name={ICON.uiCheckOn} size={16} decorative /> : <SelkirkIcon name={ICON.uiError} size={16} decorative />}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default QAQC;

